import Median from 'median-js-bridge';

/**
 * OneSignal registration through the Median JavaScript Bridge.
 *
 * These calls ONLY do something inside the Median native app wrapper
 * (iOS / Android), where OneSignal's native SDK is configured from the Median
 * dashboard. In a regular browser or the installed PWA, `median` is undefined
 * and every function here is a logged no-op — VAPID web push stays in charge.
 *
 * IMPORTANT: on a cold start the Median bridge is injected slightly AFTER our
 * JS runs, so anything that needs it must wait for it (see waitForBridge).
 */

const LOG = '[Median/OneSignal]';

let callbackOneSignalInfo: any | null = null;

declare global {
  interface Window {
    median_onesignal_info?: (info: any) => void;
  }
}

// Median calls this global after page loads in the native app. Keep the most
// recent payload as a fallback for bridge versions that do not return a Promise.
if (typeof window !== 'undefined') {
  window.median_onesignal_info = (info: any) => {
    callbackOneSignalInfo = info;
    console.log(`${LOG} device info received from native callback:`, info);
  };
}

/**
 * True when the page is running inside the Median native app wrapper.
 * Median's current guidance: rely on the bridge only, never on the user agent.
 */
export const isMedianApp = () => {
  if (typeof window === 'undefined') return false;
  try {
    return Median.isNativeApp();
  } catch {
    return false;
  }
};

const getBridgeOneSignal = (): any => {
  if (typeof window === 'undefined') return null;
  if (!isMedianApp()) return null;
  const w = window as any;
  return Median.onesignal ?? w.median?.onesignal ?? w.gonative?.onesignal ?? null;
};

export type MedianOneSignalStatus = {
  nativeApp: boolean;
  bridgeReady: boolean;
  oneSignalAvailable: boolean;
  bridgeSource: 'package' | 'injected' | 'none';
};

/** A user-safe snapshot of the native bridge state for Settings diagnostics. */
export const getMedianOneSignalStatus = (): MedianOneSignalStatus => {
  if (typeof window === 'undefined') {
    return { nativeApp: false, bridgeReady: false, oneSignalAvailable: false, bridgeSource: 'none' };
  }
  const nativeApp = isMedianApp();
  const onesignal = getBridgeOneSignal();
  const bridgeSource = !nativeApp
    ? 'none'
    : Median.onesignal
    ? 'package'
    : onesignal
    ? 'injected'
    : 'none';
  return {
    nativeApp,
    bridgeReady: nativeApp,
    oneSignalAvailable: typeof onesignal?.login === 'function',
    bridgeSource,
  };
};

/** True when the Median bridge + OneSignal plugin are available right now. */
export const hasMedianOneSignal = () => getBridgeOneSignal() !== null;

/**
 * Polls for the native OneSignal bridge, which is injected shortly after page
 * load inside the app. Resolves null in browsers (or if the plugin is off).
 */
export const waitForMedianOneSignal = async (
  timeoutMs = 10000,
  intervalMs = 250,
): Promise<any | null> => {
  const immediate = getBridgeOneSignal();
  if (immediate) return immediate;

  if (!isMedianApp()) {
    console.log(`${LOG} not running inside the native app — skipping OneSignal bridge`);
    return null;
  }

  // The package's readiness listener is the supported path for React SPAs.
  // Keep polling below as a fallback for old injected bridge builds.
  await new Promise<void>((resolve) => {
    let resolved = false;
    const finish = () => {
      if (resolved) return;
      resolved = true;
      resolve();
    };
    Median.onReady(finish);
    window.setTimeout(finish, Math.min(timeoutMs, 10000));
  });

  const packageBridge = getBridgeOneSignal();
  if (packageBridge) {
    console.log(`${LOG} package bridge is ready`);
    return packageBridge;
  }

  const deadline = Date.now() + timeoutMs;
  while (Date.now() < deadline) {
    await new Promise((r) => setTimeout(r, intervalMs));
    const bridge = getBridgeOneSignal();
    if (bridge) {
      console.log(`${LOG} bridge became available after waiting`);
      return bridge;
    }
  }
  console.warn(
    `${LOG} native app detected but the OneSignal bridge never appeared within ${timeoutMs}ms — is the OneSignal plugin enabled in the Median build?`,
  );
  return null;
};

/**
 * Links this device to the signed-in user, using their email as the OneSignal
 * external id. Waits for the bridge, so it is safe to call immediately after
 * sign-in / session restore.
 *
 * OneSignal applies the login asynchronously, so a resolved call is NOT proof
 * that the email is attached. We therefore verify by reading the device info
 * back and checking `externalId`, retrying the link once if needed.
 */
export const medianOneSignalLogin = async (email?: string | null): Promise<boolean> => {
  console.log(`${LOG} login requested`, {
    nativeApp: isMedianApp(),
    bridgeReady: hasMedianOneSignal(),
    email: email ?? null,
  });

  if (!email) {
    console.warn(`${LOG} skipped login — no email on the session`);
    return false;
  }

  const onesignal = await waitForMedianOneSignal();
  if (!onesignal) return false;

  if (typeof onesignal.login !== 'function') {
    console.warn(`${LOG} bridge has no login() — available keys:`, Object.keys(onesignal));
    return false;
  }

  // Median documents `login(externalId)`. Ask, then confirm OneSignal agrees.
  try {
    const result = await Promise.resolve(onesignal.login(email));
    if (result && typeof result === 'object' && result.success === false) {
      console.error(`${LOG} login(email) was rejected`, result);
    } else {
      console.log(`${LOG} login(email) accepted`, result ?? 'no response body');
    }
  } catch (error) {
    console.error(`${LOG} login(email) threw`, error);
  }

  if (await verifyOneSignalExternalId(email, 10000)) return true;

  // Not attached yet — try the supported external-id setter and verify again.
  const legacySet = onesignal.externalUserId?.set;
  if (typeof legacySet === 'function') {
    try {
      await Promise.resolve(legacySet.call(onesignal.externalUserId, { externalId: email }));
      console.log(`${LOG} externalUserId.set called for:`, email);
    } catch (fallbackError) {
      console.error(`${LOG} externalUserId.set failed`, fallbackError);
    }
    if (await verifyOneSignalExternalId(email, 8000)) return true;
  }

  console.warn(`${LOG} OneSignal never confirmed the email as external id:`, email);
  return false;
};

/**
 * Polls OneSignal device info until it reports `externalId` matching the email.
 * This is the only trustworthy signal that the handset belongs to the account.
 */
export const verifyOneSignalExternalId = async (
  email: string,
  timeoutMs = 10000,
): Promise<boolean> => {
  const wanted = email.trim().toLowerCase();
  const deadline = Date.now() + timeoutMs;

  do {
    const info = await medianOneSignalInfo(0, true);
    const reported = extractOneSignalExternalId(info);
    if (reported && reported.toLowerCase() === wanted) {
      console.log(`${LOG} verified external id on device:`, reported);
      return true;
    }
    if (info) console.log(`${LOG} external id not attached yet, saw:`, reported ?? 'none');
    await new Promise((resolve) => setTimeout(resolve, 750));
  } while (Date.now() < deadline);

  return false;
};


/** Unregisters the current device from the user's OneSignal identity. */
export const medianOneSignalLogout = () => {
  const onesignal = getBridgeOneSignal();
  if (!onesignal?.logout) {
    console.log(`${LOG} logout skipped — no native bridge`);
    return;
  }
  try {
    onesignal.logout();
    console.log(`${LOG} logout called`);
  } catch (error) {
    console.error(`${LOG} logout failed`, error);
  }
};

/**
 * Asks the native OS for notification permission (Android 13+ requires an
 * explicit runtime prompt) and registers the device with OneSignal.
 * No-op outside the native wrapper.
 */
export const medianOneSignalRegister = async (): Promise<boolean> => {
  const onesignal = await waitForMedianOneSignal(6000);
  if (!onesignal) {
    console.log(`${LOG} register skipped — no native bridge`);
    return false;
  }
  const candidates = ['register', 'promptForPushNotifications', 'requestPermission', 'promptPush'];
  for (const name of candidates) {
    if (typeof onesignal[name] === 'function') {
      try {
        const result = await Promise.resolve(onesignal[name]({}));
        if (result && typeof result === 'object' && result.success === false) continue;
        console.log(`${LOG} permission prompt via`, name);
        return true;
      } catch (error) {
        console.error(`${LOG} ${name} failed`, error);
      }
    }
  }
  console.warn(`${LOG} no permission-prompt method on the bridge`, Object.keys(onesignal));
  return false;
};

/**
 * Returns OneSignal device info from the native bridge, or null.
 * Pass `fresh` to ignore the cached native callback payload — needed when
 * checking whether a just-issued login has taken effect.
 */
export const medianOneSignalInfo = async (
  waitMs = 0,
  fresh = false,
): Promise<any | null> => {
  const onesignal = waitMs > 0 ? await waitForMedianOneSignal(waitMs) : getBridgeOneSignal();
  if (!onesignal) return null;
  const candidates = ['info', 'onesignalInfo', 'userInfo', 'oneSignalInfo'];
  const deadline = Date.now() + Math.max(waitMs, 1000);

  do {
    for (const name of candidates) {
      try {
        const source = onesignal[name];
        const result = typeof source === 'function'
          ? await Promise.resolve(source.call(onesignal, {}))
          : source && typeof source.then === 'function'
          ? await source
          : source;
        if (result && typeof result === 'object') {
          console.log(`${LOG} device info via ${name}:`, result);
          return result;
        }
      } catch (error) {
        console.error(`${LOG} ${name} failed`, error);
      }
    }

    // Older builds only answer through the global callback. During a fresh
    // check, give the callable methods time to answer first, then fall back to
    // the callback rather than treating callback-only builds as unlinked.
    if (!fresh && callbackOneSignalInfo) return callbackOneSignalInfo;

    await new Promise((resolve) => setTimeout(resolve, 500));
  } while (Date.now() < deadline);

  if (callbackOneSignalInfo) return callbackOneSignalInfo;
  console.warn(`${LOG} no device info returned by the bridge`);
  return null;
};

const firstString = (values: unknown[]): string | null => {
  for (const value of values) {
    if (typeof value === 'string' && value.trim().length > 0) return value.trim();
  }
  return null;
};

/**
 * The push subscription id for THIS handset. Only this value can be used to
 * target the device directly — OneSignal user/account ids cannot, and sending
 * to one comes back as "All included players are not subscribed".
 */
export const extractOneSignalSubscriptionId = (info: any): string | null => {
  if (!info || typeof info !== 'object') return null;
  return firstString([
    info?.subscription?.id,
    info?.oneSignalInfo?.subscription?.id,
    info.subscriptionId,
    info.oneSignalSubscriptionId,
    // Legacy plugin builds reported the handset as a "player".
    info.playerId,
  ]);
};

/** The email/external id OneSignal currently has attached to this handset. */
export const extractOneSignalExternalId = (info: any): string | null => {
  if (!info || typeof info !== 'object') return null;
  const knownShape = firstString([
    info.externalId,
    info.externalUserId,
    info.external_id,
    info.external_user_id,
    info?.user?.externalId,
    info?.user?.externalUserId,
    info?.oneSignalInfo?.externalId,
    info?.oneSignalInfo?.externalUserId,
  ]);
  if (knownShape) return knownShape;

  // Median plugin versions have wrapped the same value at different depths.
  // Search only for recognised external-id keys in this trusted bridge payload.
  const externalKeys = new Set(['externalid', 'externaluserid', 'external_id', 'external_user_id']);
  const seen = new Set<object>();
  const search = (value: unknown, depth: number): string | null => {
    if (!value || typeof value !== 'object' || depth > 4 || seen.has(value)) return null;
    seen.add(value);
    for (const [key, nested] of Object.entries(value)) {
      if (externalKeys.has(key.toLowerCase()) && typeof nested === 'string' && nested.trim()) {
        return nested.trim();
      }
      const found = search(nested, depth + 1);
      if (found) return found;
    }
    return null;
  };
  return search(info, 0);
};

/** OneSignal's own account id — useful for diagnostics, never for targeting. */
export const extractOneSignalUserId = (info: any): string | null => {
  if (!info || typeof info !== 'object') return null;
  return firstString([info.oneSignalId, info?.oneSignalInfo?.oneSignalId]);
};

/** True when the handset's push subscription is actually opted in. */
export const isOneSignalSubscriptionActive = (info: any): boolean => {
  if (!info || typeof info !== 'object') return false;
  const sub = info.subscription ?? info?.oneSignalInfo?.subscription;
  if (sub && typeof sub === 'object' && typeof sub.optedIn === 'boolean') return sub.optedIn;
  if (typeof info.isSubscribed === 'boolean') return info.isSubscribed;
  // Unknown shape: assume active so older builds still save their id.
  return Boolean(extractOneSignalSubscriptionId(info));
};


/**
 * Sync the app icon badge number on the phone app.
 * No-op on the website and PWA.
 */
export const setAppBadge = (count: number): void => {
  if (!isMedianApp()) return;
  try {
    const os = getBridgeOneSignal() as any;
    os?.badge?.set?.(Math.max(0, Math.floor(count)));
  } catch (error) {
    console.warn('[median] failed to set app badge', error);
  }
};
